import { useEffect, useMemo, useState } from "react";
import type { CSSProperties, FormEvent, ReactNode } from "react";
import {
  Bell,
  BookOpen,
  CalendarDays,
  Check,
  ChevronRight,
  Clock,
  Cloud,
  CloudOff,
  Download,
  Edit3,
  Home,
  LogIn,
  LogOut,
  Plus,
  RefreshCw,
  RotateCcw,
  Settings,
  Shirt,
  Wand2,
  Sparkles,
  Trash2,
  Upload,
  Utensils,
  WifiOff,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { CATEGORIES, GROOMING_INTERVALS, LEARNING_TYPES, MODULE_LABEL } from "./constants";
import type {
  GroomingItem,
  LearningItem,
  ModuleKey,
  NutritionItem,
  RitualSnapshot,
  TaskSchedule,
  ToastState,
  WardrobeItem,
} from "./types";
import { useAuth } from "./hooks/useAuth";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { useRitualStore } from "./hooks/useRitualStore";
import { useReminderNotifications } from "./hooks/useReminderNotifications";
import { buildExport, parseImport } from "./lib/exportImport";
import { daysSince, formatLongDate, greeting, localDateKey } from "./lib/date";
import { newId } from "./lib/id";
import { createWardrobeImageUrl, isRemoteStoragePath, uploadWardrobeImage } from "./lib/wardrobeStorage";
import {
  defaultSchedule,
  defaultWindow,
  DueOccurrence,
  occurrencesForItem,
  scheduleSummary,
} from "./lib/schedule";

type Tab = "today" | ModuleKey | "settings";

const TABS: Array<{ id: Tab; label: string; icon: LucideIcon }> = [
  { id: "today", label: "Today", icon: Home },
  { id: "nutrition", label: "Nutrition", icon: Utensils },
  { id: "grooming", label: "Grooming", icon: Sparkles },
  { id: "wardrobe", label: "Wardrobe", icon: Shirt },
  { id: "learning", label: "Learning", icon: BookOpen },
  { id: "settings", label: "Settings", icon: Settings },
];

const statusLabel = {
  idle: "Local",
  offline: "Offline",
  syncing: "Syncing",
  synced: "Synced",
  error: "Sync issue",
};

const WEEKDAYS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const WARDROBE_STARTERS = [
  { name: "White linen shirt", category: "Tops", color: "White", notes: "Easy work / casual base.", image_url: "/wardrobe/white-linen-shirt.png" },
  { name: "Black crew neck tee", category: "Tops", color: "Black", notes: "Default casual top.", image_url: "/wardrobe/black-crew-tee.png" },
  { name: "Blue denim jeans", category: "Bottoms", color: "Blue", notes: "Pairs with most tops.", image_url: "/wardrobe/blue-denim-jeans.png" },
  { name: "Charcoal chinos", category: "Bottoms", color: "Charcoal", notes: "Office-friendly fallback.", image_url: "/wardrobe/charcoal-chinos.png" },
  { name: "Navy overshirt", category: "Outerwear", color: "Navy", notes: "Light layer for evenings.", image_url: "/wardrobe/navy-overshirt.png" },
  { name: "Brown belt", category: "Accessories", color: "Brown", notes: "Match with brown shoes.", image_url: "/wardrobe/brown-belt.png" },
];

const WARDROBE_FALLBACK_IMAGES: Record<string, string> = {
  Tops: "/wardrobe/white-linen-shirt.png",
  Bottoms: "/wardrobe/blue-denim-jeans.png",
  Outerwear: "/wardrobe/navy-overshirt.png",
  Accessories: "/wardrobe/brown-belt.png",
  Formal: "/wardrobe/charcoal-chinos.png",
  Ethnic: "/wardrobe/white-linen-shirt.png",
  Other: "/wardrobe/black-crew-tee.png",
};

function todayForForms() {
  return localDateKey(new Date());
}

function nutritionScheduleDefault() {
  return defaultSchedule("daily", todayForForms(), { windows: [defaultWindow("Take", "13:00", "14:00")] });
}

function groomingScheduleDefault(intervalDays = 3) {
  return defaultSchedule(intervalDays === 1 ? "daily" : "interval", todayForForms(), {
    interval_days: intervalDays,
    windows: [defaultWindow("Morning", "08:00", "10:00")],
  });
}

function wardrobeScheduleDefault() {
  return defaultSchedule("weekly", todayForForms(), {
    weekdays: [new Date().getDay()],
    windows: [defaultWindow("Wear", "08:00", "11:00")],
  });
}

function wardrobeImageFor(item: Pick<WardrobeItem, "image_url" | "category" | "name">) {
  return item.image_url || WARDROBE_FALLBACK_IMAGES[item.category] || WARDROBE_FALLBACK_IMAGES.Other;
}

function learningScheduleDefault() {
  return defaultSchedule("daily", todayForForms(), { windows: [defaultWindow("Learn", "20:00", "21:00")] });
}

function App() {
  const online = useOnlineStatus();
  const auth = useAuth();
  const store = useRitualStore(auth.user?.id ?? null, online);
  const [tab, setTab] = useState<Tab>("today");
  const [toast, setToast] = useState<ToastState | null>(null);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [authUsername, setAuthUsername] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [now, setNow] = useState(() => new Date());
  const authButtonLabel = auth.isSubmitting
    ? authMode === "login"
      ? "Signing in..."
      : "Creating account..."
    : authMode === "login"
      ? "Login"
      : "Sign up";

  const timezone = store.snapshot?.profile.timezone;
  const displayName = store.snapshot?.profile.display_name?.trim() || "there";
  const visible = store.visible;
  const loggedKeys = store.loggedKeys;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const occurrences = useMemo(() => {
    const snapshot = store.snapshot;
    if (!visible || !snapshot) return [];
    return [
      ...visible.nutrition.flatMap((item) =>
        occurrencesForItem({
          category: "nutrition",
          item,
          title: item.name,
          subtitle: item.quantity || item.category,
          date: store.today,
          logs: snapshot.logs,
          loggedKeys,
          now,
          timezone,
        }),
      ),
      ...visible.grooming.flatMap((item) =>
        occurrencesForItem({
          category: "grooming",
          item,
          title: item.name,
          subtitle: scheduleSummary(item.schedule),
          date: store.today,
          logs: snapshot.logs,
          loggedKeys,
          now,
          timezone,
        }),
      ),
      ...visible.wardrobe.flatMap((item) =>
        occurrencesForItem({
          category: "wardrobe",
          item,
          title: item.name,
          subtitle: item.category,
          date: store.today,
          logs: snapshot.logs,
          loggedKeys,
          now,
          timezone,
        }),
      ),
      ...visible.learning
        .filter((item) => item.active && item.progress < 100)
        .flatMap((item) =>
          occurrencesForItem({
            category: "learning",
            item,
            title: item.title,
            subtitle: `${item.category} · ${item.progress}%`,
            date: store.today,
            logs: snapshot.logs,
            loggedKeys,
            now,
            timezone,
          }),
        ),
    ].sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [loggedKeys, now, store.snapshot, store.today, timezone, visible]);

  const notifications = useReminderNotifications(occurrences);

  const reminders = useMemo(() => {
    const counts = { nutrition: 0, grooming: 0, wardrobe: 0, learning: 0 };
    for (const occurrence of occurrences) {
      if (occurrence.status !== "done") counts[occurrence.category] += 1;
    }
    return counts;
  }, [occurrences]);

  const deleteWithUndo = async (module: ModuleKey, id: string, label: string) => {
    await store.softDelete(module, id);
    setToast({
      message: `${label} moved out of your active list.`,
      actionLabel: "Undo",
      onAction: () => {
        void store.restoreItem(module, id);
        setToast(null);
      },
    });
  };

  if (auth.loading || store.loading || !store.snapshot || !visible) {
    return (
      <main className="splash">
        <div className="brand-mark">R</div>
        <p>Preparing Ritual...</p>
      </main>
    );
  }

  if (auth.isConfigured && !auth.user) {
    return (
      <main className="splash">
        <div className="brand-mark">R</div>
        <h1>Sign in to Ritual</h1>
        <p>Use your username (email) and password.</p>
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            const username = authUsername.trim();
            if (!username || !authPassword) return;

            if (authMode === "login") {
              void auth.requestPasswordSignIn(username, authPassword);
              return;
            }

            void auth.requestPasswordSignUp(username, authPassword);
          }}
        >
          <TextField label="Username (email)" type="email" value={authUsername} onChange={setAuthUsername} />
          <TextField label="Password" type="password" value={authPassword} onChange={setAuthPassword} />
          <button className="primary-button" type="submit" disabled={auth.isSubmitting}>
            <LogIn size={16} /> {authButtonLabel}
          </button>
          <button
            className="secondary-button"
            type="button"
            disabled={auth.isSubmitting}
            onClick={() => setAuthMode((mode) => (mode === "login" ? "signup" : "login"))}
          >
            {authMode === "login" ? "Need an account? Sign up" : "Already have an account? Login"}
          </button>
          {auth.message && <p className="success">{auth.message}</p>}
          {auth.error && <p className="danger-text">{auth.error}</p>}
        </form>
      </main>
    );
  }

  return (
    <div className="app">
      <div className="app-shell">
        <header className="topbar">
          <div>
            <p className="eyebrow">{formatLongDate(new Date(), timezone)}</p>
            <h1>
              {greeting(new Date(), timezone)}, {displayName}
            </h1>
          </div>
          <button className={`sync-pill ${store.syncStatus}`} onClick={() => void store.syncNow()}>
            {online ? <Cloud size={16} /> : <WifiOff size={16} />}
            <span>{statusLabel[store.syncStatus]}</span>
            {store.pendingCount > 0 && <b>{store.pendingCount}</b>}
          </button>
        </header>

        <section className="content" aria-live="polite">
          {tab === "today" && (
            <Dashboard
              visible={visible}
              occurrences={occurrences}
              reminders={reminders}
              timezone={timezone}
              today={store.today}
              isDoneToday={store.isDoneToday}
              logToday={store.logToday}
              markGroomingDone={store.markGroomingDone}
              markWardrobeWorn={store.markWardrobeWorn}
              setTab={setTab}
            />
          )}
          {tab === "nutrition" && (
            <NutritionPage
              items={visible.nutrition}
              isDoneToday={store.isDoneToday}
              onSave={store.saveNutrition}
              onLog={(id, occurrenceId) => store.logToday("nutrition", id, occurrenceId)}
              onDelete={(item) => deleteWithUndo("nutrition", item.id, item.name)}
            />
          )}
          {tab === "grooming" && (
            <GroomingPage
              items={visible.grooming}
              timezone={timezone}
              isDoneToday={store.isDoneToday}
              onSave={store.saveGrooming}
              onDone={store.markGroomingDone}
              onDelete={(item) => deleteWithUndo("grooming", item.id, item.name)}
            />
          )}
          {tab === "wardrobe" && (
            <WardrobePage
              items={visible.wardrobe}
              userId={auth.user?.id ?? null}
              timezone={timezone}
              isDoneToday={store.isDoneToday}
              onSave={store.saveWardrobe}
              onWorn={store.markWardrobeWorn}
              onDelete={(item) => deleteWithUndo("wardrobe", item.id, item.name)}
            />
          )}
          {tab === "learning" && (
            <LearningPage
              items={visible.learning}
              isDoneToday={store.isDoneToday}
              onSave={store.saveLearning}
              onProgress={store.saveLearningProgress}
              onLog={(id, occurrenceId) => store.logToday("learning", id, occurrenceId)}
              onDelete={(item) => deleteWithUndo("learning", item.id, item.title)}
            />
          )}
          {tab === "settings" && (
            <SettingsPage
              auth={auth}
              online={online}
              snapshot={store.snapshot}
              syncStatus={store.syncStatus}
              syncError={store.syncError}
              pendingCount={store.pendingCount}
              notifications={notifications}
              onProfileSave={store.saveProfile}
              onImport={store.replaceSnapshot}
              onSync={() => store.syncNow()}
            />
          )}
        </section>

        <BottomNav active={tab} reminders={reminders} onTab={setTab} />
        {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
      </div>
    </div>
  );
}

function Dashboard({
  visible,
  occurrences,
  reminders,
  timezone,
  today,
  isDoneToday,
  logToday,
  markGroomingDone,
  markWardrobeWorn,
  setTab,
}: {
  visible: {
    nutrition: NutritionItem[];
    grooming: GroomingItem[];
    wardrobe: WardrobeItem[];
    learning: LearningItem[];
  };
  occurrences: DueOccurrence[];
  reminders: Record<ModuleKey, number>;
  timezone?: string;
  today: string;
  isDoneToday: (category: ModuleKey, itemId: string, occurrenceId?: string) => boolean;
  logToday: (category: ModuleKey, itemId: string, occurrenceId?: string) => Promise<void>;
  markGroomingDone: (id: string, occurrenceId?: string) => Promise<void>;
  markWardrobeWorn: (id: string, occurrenceId?: string) => Promise<void>;
  setTab: (tab: Tab) => void;
}) {
  const missed = occurrences.filter((occurrence) => occurrence.status === "missed");
  const open = occurrences.filter((occurrence) => occurrence.status === "open");
  const upcoming = occurrences.filter((occurrence) => occurrence.status === "upcoming");
  const pending = occurrences.filter((occurrence) => occurrence.status !== "done");
  const wardrobeSuggestion = [...visible.wardrobe].sort((a, b) => {
    const da = daysSince(a.last_worn, timezone) ?? 999;
    const db = daysSince(b.last_worn, timezone) ?? 999;
    return db - da;
  })[0];

  const total = occurrences.length;
  const done = occurrences.filter((occurrence) => occurrence.status === "done").length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const completeOccurrence = (occurrence: DueOccurrence) => {
    if (occurrence.category === "grooming") return markGroomingDone(occurrence.itemId, occurrence.occurrenceId);
    if (occurrence.category === "wardrobe") return markWardrobeWorn(occurrence.itemId, occurrence.occurrenceId);
    return logToday(occurrence.category, occurrence.itemId, occurrence.occurrenceId);
  };

  return (
    <div className="stack">
      <section className="hero-panel">
        <div className="ring" style={{ "--pct": pct } as CSSProperties}>
          <span>{pct}%</span>
        </div>
        <div>
          <p className="eyebrow">Today</p>
          <h2>{done} of {Math.max(total, 0)} scheduled windows done</h2>
          <p className="muted">{today} · notifications after a missed time window</p>
        </div>
      </section>

      <div className="reminder-grid">
        {Object.entries(reminders).map(([key, count]) => (
          <button key={key} className="stat-tile" onClick={() => setTab(key as ModuleKey)}>
            <span>{MODULE_LABEL[key as ModuleKey]}</span>
            <strong>{count}</strong>
          </button>
        ))}
      </div>

      {wardrobeSuggestion && (
        <Panel
          icon={Shirt}
          title="Wear Today"
          action={<IconButton label="Open wardrobe" icon={ChevronRight} onClick={() => setTab("wardrobe")} />}
        >
          <ItemLine
            title={wardrobeSuggestion.name}
            meta={
              wardrobeSuggestion.last_worn
                ? `Last worn ${daysSince(wardrobeSuggestion.last_worn, timezone)} days ago`
                : "Never worn"
            }
            action={
              <DoneButton
                done={isDoneToday("wardrobe", wardrobeSuggestion.id, wardrobeSuggestion.schedule.windows[0]?.id)}
                onClick={() => markWardrobeWorn(wardrobeSuggestion.id, wardrobeSuggestion.schedule.windows[0]?.id)}
              />
            }
          />
        </Panel>
      )}

      <DashboardPanel
        icon={Bell}
        title="Missed Windows"
        items={missed.slice(0, 5)}
        empty="No missed windows."
        more={missed.length > 5 ? missed.length - 5 : 0}
        onMore={() => setTab("today")}
        render={(occurrence) => (
          <ItemLine
            key={occurrence.key}
            title={occurrence.title}
            meta={`${MODULE_LABEL[occurrence.category]} · ${occurrence.label} ${occurrence.startTime}-${occurrence.endTime}`}
            tone="danger"
            action={<DoneButton done={occurrence.done} onClick={() => completeOccurrence(occurrence)} />}
          />
        )}
      />

      <DashboardPanel
        icon={Clock}
        title="Open Now"
        items={open.slice(0, 5)}
        empty="Nothing is inside its time window right now."
        more={open.length > 5 ? open.length - 5 : 0}
        onMore={() => setTab("today")}
        render={(occurrence) => (
          <ItemLine
            key={occurrence.key}
            title={occurrence.title}
            meta={`${MODULE_LABEL[occurrence.category]} · ${occurrence.label} until ${occurrence.endTime}`}
            tone="warn"
            action={<DoneButton done={occurrence.done} onClick={() => completeOccurrence(occurrence)} />}
          />
        )}
      />

      <DashboardPanel
        icon={CalendarDays}
        title="Later Today"
        items={upcoming.slice(0, 5)}
        empty={pending.length === 0 ? "Everything scheduled today is done." : "Nothing else later today."}
        more={upcoming.length > 5 ? upcoming.length - 5 : 0}
        onMore={() => setTab("today")}
        render={(occurrence) => (
          <ItemLine
            key={occurrence.key}
            title={occurrence.title}
            meta={`${MODULE_LABEL[occurrence.category]} · ${occurrence.label} ${occurrence.startTime}-${occurrence.endTime}`}
            action={<DoneButton done={occurrence.done} onClick={() => completeOccurrence(occurrence)} />}
          />
        )}
      />
    </div>
  );
}

function NutritionPage({
  items,
  isDoneToday,
  onSave,
  onLog,
  onDelete,
}: {
  items: NutritionItem[];
  isDoneToday: (category: ModuleKey, itemId: string, occurrenceId?: string) => boolean;
  onSave: (input: { id?: string; name: string; category: string; quantity: string; schedule?: TaskSchedule }) => Promise<void>;
  onLog: (id: string, occurrenceId?: string) => Promise<void>;
  onDelete: (item: NutritionItem) => void;
}) {
  const emptyDraft = () => ({ name: "", category: CATEGORIES.nutrition[0], quantity: "", schedule: nutritionScheduleDefault() });
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const grouped = CATEGORIES.nutrition.filter((cat) => items.some((item) => item.category === cat));

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft.name.trim()) return;
    await onSave({ id: editingId ?? undefined, ...draft });
    setDraft(emptyDraft());
    setEditingId(null);
  };

  return (
    <ModuleScaffold
      title="Nutrition"
      icon={Utensils}
      count={items.length}
      form={
        <form className="form-grid" onSubmit={save}>
          <TextField label="Name" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          <SelectField
            label="Category"
            value={draft.category}
            options={CATEGORIES.nutrition}
            onChange={(category) => setDraft({ ...draft, category })}
          />
          <TextField label="Quantity / notes" value={draft.quantity} onChange={(quantity) => setDraft({ ...draft, quantity })} />
          <ScheduleEditor schedule={draft.schedule} onChange={(schedule) => setDraft({ ...draft, schedule })} />
          <FormActions editing={Boolean(editingId)} onCancel={() => { setDraft(emptyDraft()); setEditingId(null); }} />
        </form>
      }
    >
      {items.length === 0 && <EmptyState icon={Utensils} text="Add supplements, fruits, dry fruits, eggs, or other health items." />}
      {grouped.map((category) => (
        <div className="group" key={category}>
          <h3>{category}</h3>
          {items.filter((item) => item.category === category).map((item) => (
            <ItemLine
              key={item.id}
              title={item.name}
              meta={`${item.quantity ? `${item.quantity} · ` : ""}${scheduleSummary(item.schedule)}`}
              faded={item.schedule.windows.every((window) => isDoneToday("nutrition", item.id, window.id))}
              action={<RowActions
                done={item.schedule.windows.every((window) => isDoneToday("nutrition", item.id, window.id))}
                onDone={() => onLog(item.id, item.schedule.windows[0]?.id)}
                onEdit={() => {
                  setEditingId(item.id);
                  setDraft({ name: item.name, category: item.category, quantity: item.quantity, schedule: item.schedule });
                }}
                onDelete={() => onDelete(item)}
              />}
            />
          ))}
        </div>
      ))}
    </ModuleScaffold>
  );
}

function GroomingPage({
  items,
  timezone,
  isDoneToday,
  onSave,
  onDone,
  onDelete,
}: {
  items: GroomingItem[];
  timezone?: string;
  isDoneToday: (category: ModuleKey, itemId: string, occurrenceId?: string) => boolean;
  onSave: (input: { id?: string; name: string; interval_days: number; last_done?: string | null; schedule?: TaskSchedule }) => Promise<void>;
  onDone: (id: string, occurrenceId?: string) => Promise<void>;
  onDelete: (item: GroomingItem) => void;
}) {
  const emptyDraft = () => ({ name: "", interval_days: 3, schedule: groomingScheduleDefault(3) });
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft.name.trim()) return;
    const existing = items.find((item) => item.id === editingId);
    await onSave({ id: editingId ?? undefined, ...draft, last_done: existing?.last_done ?? null });
    setDraft(emptyDraft());
    setEditingId(null);
  };

  return (
    <ModuleScaffold
      title="Grooming"
      icon={Sparkles}
      count={items.length}
      form={
        <form className="form-grid" onSubmit={save}>
          <TextField label="Habit" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          <div className="field">
            <span>Repeat every</span>
            <div className="chips">
              {GROOMING_INTERVALS.map((days) => (
                <button
                  className={draft.interval_days === days ? "chip active" : "chip"}
                  key={days}
                  type="button"
                  onClick={() => setDraft({ ...draft, interval_days: days, schedule: { ...draft.schedule, frequency: days === 1 ? "daily" : "interval", interval_days: days } })}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>
          <ScheduleEditor schedule={draft.schedule} onChange={(schedule) => setDraft({ ...draft, schedule })} />
          <FormActions editing={Boolean(editingId)} onCancel={() => { setDraft(emptyDraft()); setEditingId(null); }} />
        </form>
      }
    >
      {items.map((item) => {
        const age = daysSince(item.last_done, timezone);
        const overdue = age === null || age >= item.interval_days;
        return (
          <ItemLine
            key={item.id}
            title={item.name}
            meta={`${age === null ? "Never done" : `${age}d ago`} · ${scheduleSummary(item.schedule)}`}
            tone={overdue ? "danger" : "ok"}
            badge={overdue && age !== 0 ? "Due now" : undefined}
            action={<RowActions
              done={item.schedule.windows.every((window) => isDoneToday("grooming", item.id, window.id))}
              onDone={() => onDone(item.id, item.schedule.windows[0]?.id)}
              onEdit={() => {
                setEditingId(item.id);
                setDraft({ name: item.name, interval_days: item.interval_days, schedule: item.schedule });
              }}
              onDelete={() => onDelete(item)}
            />}
          />
        );
      })}
    </ModuleScaffold>
  );
}

function WardrobePage({
  items,
  userId,
  timezone,
  isDoneToday,
  onSave,
  onWorn,
  onDelete,
}: {
  items: WardrobeItem[];
  userId: string | null;
  timezone?: string;
  isDoneToday: (category: ModuleKey, itemId: string, occurrenceId?: string) => boolean;
  onSave: (input: { id?: string; name: string; category: string; color: string; notes: string; image_url?: string; schedule?: TaskSchedule }) => Promise<void>;
  onWorn: (id: string, occurrenceId?: string) => Promise<void>;
  onDelete: (item: WardrobeItem) => void;
}) {
  const emptyDraft = () => ({ name: "", category: CATEGORIES.wardrobe[0], color: "", notes: "", image_url: "", schedule: wardrobeScheduleDefault() });
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filter, setFilter] = useState("All");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const neglected = useMemo(
    () =>
      items.filter((item) => {
        const age = daysSince(item.last_worn, timezone);
        return age === null || age >= 30;
      }),
    [items, timezone],
  );
  const filtered = useMemo(() => {
    const source = filter === "All" ? items : items.filter((item) => item.category === filter);
    return [...source].sort((a, b) => {
      const da = daysSince(a.last_worn, timezone) ?? 999;
      const db = daysSince(b.last_worn, timezone) ?? 999;
      return db - da;
    });
  }, [filter, items, timezone]);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft.name.trim()) return;
    setImageError(null);
    const id = editingId ?? newId();
    let image_url = draft.image_url;
    try {
      if (imageFile) {
        if (!userId) throw new Error("Sign in before uploading wardrobe photos.");
        image_url = await uploadWardrobeImage(userId, id, imageFile);
      }
      await onSave({ id, ...draft, image_url });
    } catch (error) {
      setImageError(error instanceof Error ? error.message : "Could not save wardrobe image.");
      return;
    }
    setDraft(emptyDraft());
    setImageFile(null);
    setEditingId(null);
  };

  const addStarter = async (starter: (typeof WARDROBE_STARTERS)[number]) => {
    await onSave({ ...starter, schedule: wardrobeScheduleDefault() });
  };

  return (
    <ModuleScaffold
      title="Wardrobe"
      icon={Shirt}
      count={items.length}
      notice={neglected.length ? `${neglected.length} item${neglected.length > 1 ? "s" : ""} not worn in 30+ days.` : undefined}
      form={
        <form className="form-grid" onSubmit={save}>
          <TextField label="Item" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          <SelectField label="Category" value={draft.category} options={CATEGORIES.wardrobe} onChange={(category) => setDraft({ ...draft, category })} />
          <TextField label="Color / description" value={draft.color} onChange={(color) => setDraft({ ...draft, color })} />
          <TextField label="Image URL" value={draft.image_url} onChange={(image_url) => setDraft({ ...draft, image_url })} />
          <label className="field file-upload-field">
            <span>Upload photo</span>
            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                setImageError(null);
                setImageFile(event.target.files?.[0] ?? null);
              }}
            />
            <small>
              {imageFile
                ? imageFile.name
                : userId
                  ? "Private Supabase Storage upload"
                  : "Sign in to upload private photos"}
            </small>
          </label>
          {imageError && <p className="danger-text">{imageError}</p>}
          <TextField label="Notes" value={draft.notes} onChange={(notes) => setDraft({ ...draft, notes })} />
          <ScheduleEditor schedule={draft.schedule} onChange={(schedule) => setDraft({ ...draft, schedule })} />
          <FormActions editing={Boolean(editingId)} onCancel={() => { setDraft(emptyDraft()); setEditingId(null); setImageFile(null); setImageError(null); }} />
        </form>
      }
    >
      {items.length === 0 && (
        <Panel icon={Wand2} title="Starter Closet">
          <div className="starter-grid">
            {WARDROBE_STARTERS.map((starter) => (
              <button key={starter.name} type="button" className="starter-tile" onClick={() => void addStarter(starter)}>
                <img src={starter.image_url} alt="" loading="lazy" />
                <strong>{starter.name}</strong>
                <span>{starter.category} · {starter.color}</span>
              </button>
            ))}
          </div>
        </Panel>
      )}
      <div className="chips scroll">
        {["All", ...CATEGORIES.wardrobe].map((category) => (
          <button className={filter === category ? "chip active" : "chip"} key={category} type="button" onClick={() => setFilter(category)}>
            {category}
          </button>
        ))}
      </div>
      {filtered.length === 0 && <EmptyState icon={Shirt} text="Add clothes to start rotating what you already own, or tap a starter above." />}
      <div className="wardrobe-grid">
        {filtered.map((item) => (
          <WardrobeCard
            key={item.id}
            item={item}
            image={wardrobeImageFor(item)}
            age={daysSince(item.last_worn, timezone)}
            done={item.schedule.windows.every((window) => isDoneToday("wardrobe", item.id, window.id))}
            onDone={() => onWorn(item.id, item.schedule.windows[0]?.id)}
            onEdit={() => {
              setEditingId(item.id);
              setDraft({
                name: item.name,
                category: item.category,
                color: item.color,
                notes: item.notes,
                image_url: item.image_url,
                schedule: item.schedule,
              });
              setImageFile(null);
              setImageError(null);
            }}
            onDelete={() => onDelete(item)}
          />
        ))}
      </div>
    </ModuleScaffold>
  );
}

function WardrobeCard({
  item,
  image,
  age,
  done,
  onDone,
  onEdit,
  onDelete,
}: {
  item: WardrobeItem;
  image: string;
  age: number | null;
  done: boolean;
  onDone: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const tone = age === null || age >= 30 ? "rest" : age >= 14 ? "rotate" : "fresh";
  return (
    <article className={`wardrobe-card ${tone}`}>
      <div className="wardrobe-photo">
        <WardrobeImage src={image} alt={item.name} />
        <span>{age === null ? "Never worn" : `${age}d ago`}</span>
      </div>
      <div className="wardrobe-card-body">
        <div>
          <strong>{item.name}</strong>
          <p>{item.category}{item.color ? ` · ${item.color}` : ""}</p>
        </div>
        <RowActions done={done} onDone={onDone} onEdit={onEdit} onDelete={onDelete} />
      </div>
      <small>{item.notes || scheduleSummary(item.schedule)}</small>
    </article>
  );
}

function WardrobeImage({ src, alt }: { src: string; alt: string }) {
  const [signedUrl, setSignedUrl] = useState(src);

  useEffect(() => {
    let mounted = true;
    setSignedUrl(src);

    if (!isRemoteStoragePath(src)) return undefined;

    createWardrobeImageUrl(src)
      .then((url) => {
        if (mounted && url) setSignedUrl(url);
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      mounted = false;
    };
  }, [src]);

  return <img src={signedUrl} alt={alt} loading="lazy" />;
}

function LearningPage({
  items,
  isDoneToday,
  onSave,
  onProgress,
  onLog,
  onDelete,
}: {
  items: LearningItem[];
  isDoneToday: (category: ModuleKey, itemId: string, occurrenceId?: string) => boolean;
  onSave: (input: { id?: string; title: string; type: string; category: string; notes: string; progress: number; active: boolean; schedule?: TaskSchedule }) => Promise<void>;
  onProgress: (id: string, progress: number) => Promise<void>;
  onLog: (id: string, occurrenceId?: string) => Promise<void>;
  onDelete: (item: LearningItem) => void;
}) {
  const emptyDraft = () => ({ title: "", type: LEARNING_TYPES[0], category: CATEGORIES.learning[0], notes: "", progress: 0, active: true, schedule: learningScheduleDefault() });
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const activeItems = items.filter((item) => item.active && item.progress < 100);
  const completed = items.filter((item) => !item.active || item.progress >= 100);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft.title.trim()) return;
    await onSave({ id: editingId ?? undefined, ...draft });
    setDraft(emptyDraft());
    setEditingId(null);
  };

  return (
    <ModuleScaffold
      title="Learning"
      icon={BookOpen}
      count={items.length}
      form={
        <form className="form-grid" onSubmit={save}>
          <TextField label="Title" value={draft.title} onChange={(title) => setDraft({ ...draft, title })} />
          <SelectField label="Type" value={draft.type} options={LEARNING_TYPES} onChange={(type) => setDraft({ ...draft, type })} />
          <SelectField label="Category" value={draft.category} options={CATEGORIES.learning} onChange={(category) => setDraft({ ...draft, category })} />
          <TextField label="Notes / link" value={draft.notes} onChange={(notes) => setDraft({ ...draft, notes })} />
          <label className="toggle-line">
            <input type="checkbox" checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} />
            <span>Active</span>
          </label>
          <ScheduleEditor schedule={draft.schedule} onChange={(schedule) => setDraft({ ...draft, schedule })} />
          <FormActions editing={Boolean(editingId)} onCancel={() => { setDraft(emptyDraft()); setEditingId(null); }} />
        </form>
      }
    >
      {items.length === 0 && <EmptyState icon={BookOpen} text="Add books, courses, podcasts, or videos you want to keep moving." />}
      {activeItems.length > 0 && <h3 className="section-label">Active</h3>}
      {activeItems.map((item) => (
        <LearningCard
          key={item.id}
          item={item}
          done={item.schedule.windows.every((window) => isDoneToday("learning", item.id, window.id))}
          onLog={(occurrenceId) => onLog(item.id, occurrenceId)}
          onProgress={onProgress}
          onEdit={() => {
            setEditingId(item.id);
            setDraft({
              title: item.title,
              type: item.type,
              category: item.category,
              notes: item.notes,
              progress: item.progress,
              active: item.active,
              schedule: item.schedule,
            });
          }}
          onDelete={() => onDelete(item)}
        />
      ))}
      {completed.length > 0 && <h3 className="section-label">Completed / Paused</h3>}
      {completed.map((item) => (
        <ItemLine
          key={item.id}
          title={item.title}
          meta={`${item.type} · ${item.category} · ${item.progress}%`}
          action={<RowActions
            done={item.progress >= 100}
            onEdit={() => {
              setEditingId(item.id);
              setDraft({
                title: item.title,
                type: item.type,
                category: item.category,
                notes: item.notes,
              progress: item.progress,
              active: item.active,
              schedule: item.schedule,
            });
            }}
            onDelete={() => onDelete(item)}
          />}
        />
      ))}
    </ModuleScaffold>
  );
}

function SettingsPage({
  auth,
  online,
  snapshot,
  syncStatus,
  syncError,
  pendingCount,
  notifications,
  onProfileSave,
  onImport,
  onSync,
}: {
  auth: ReturnType<typeof useAuth>;
  online: boolean;
  snapshot: NonNullable<ReturnType<typeof useRitualStore>["snapshot"]>;
  syncStatus: string;
  syncError: string | null;
  pendingCount: number;
  notifications: ReturnType<typeof useReminderNotifications>;
  onProfileSave: (displayName: string, timezone: string) => Promise<void>;
  onImport: (snapshot: RitualSnapshot) => Promise<void>;
  onSync: () => Promise<void> | void;
}) {
  const [displayName, setDisplayName] = useState(snapshot.profile.display_name);
  const [timezone, setTimezone] = useState(snapshot.profile.timezone);
  const [importError, setImportError] = useState<string | null>(null);

  const exportJson = () => {
    const blob = new Blob([buildExport(snapshot)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ritual-backup-${snapshot.last_synced_at?.slice(0, 10) ?? "local"}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const importFile = async (file: File | undefined) => {
    if (!file) return;
    setImportError(null);
    try {
      const text = await file.text();
      await onImport(parseImport(text));
    } catch (error) {
      setImportError(error instanceof Error ? error.message : "Import failed.");
    }
  };

  return (
    <div className="stack">
      <Panel icon={Settings} title="Account">
        {auth.isConfigured ? (
          auth.user ? (
            <div className="account-row">
              <div>
                <strong>{auth.user.email}</strong>
                <p className="muted">Cloud sync is tied to this email.</p>
              </div>
              <button className="secondary-button" onClick={() => void auth.signOut()}>
                <LogOut size={16} /> Sign out
              </button>
            </div>
          ) : (
            <p className="muted">Sign in on the launch screen to connect this device.</p>
          )
        ) : (
          <div className="notice">
            Add `VITE_SUPABASE_URL` and `VITE_SUPABASE_ANON_KEY` before deploying to enable cloud sync.
          </div>
        )}
      </Panel>

      <Panel icon={Sparkles} title="Profile">
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            void onProfileSave(displayName, timezone);
          }}
        >
          <TextField label="Display name" value={displayName} onChange={setDisplayName} />
          <TextField label="Timezone" value={timezone} onChange={setTimezone} />
          <button className="primary-button" type="submit">
            <Check size={16} /> Save profile
          </button>
        </form>
      </Panel>

      <Panel icon={online ? Cloud : CloudOff} title="Sync">
        <div className="sync-details">
          <span>Status</span>
          <strong>{online ? statusLabel[syncStatus as keyof typeof statusLabel] ?? syncStatus : "Offline"}</strong>
          <span>Pending writes</span>
          <strong>{pendingCount}</strong>
          <span>Last sync</span>
          <strong>{snapshot.last_synced_at ? new Date(snapshot.last_synced_at).toLocaleString() : "Never"}</strong>
        </div>
        {syncError && <p className="danger-text">{syncError}</p>}
        <button className="secondary-button" onClick={() => void onSync()}>
          <RefreshCw size={16} /> Sync now
        </button>
      </Panel>

      <Panel icon={Bell} title="Notifications">
        <div className="sync-details">
          <span>Permission</span>
          <strong>{notifications.supported ? notifications.permission : "Unsupported"}</strong>
          <span>Delivery</span>
          <strong>
            {notifications.pushSupported && notifications.standalone
              ? "Can work in background (with push server)"
              : "While app is open/running"}
          </strong>
        </div>
        <button
          className="secondary-button"
          disabled={!notifications.supported || notifications.permission === "granted"}
          onClick={() => void notifications.requestPermission()}
        >
          <Bell size={16} /> Enable reminders
        </button>
        <p className="muted">
          Ritual sends local reminders after a scheduled time window is missed. On iPhone, closed-app delivery requires installing the app to Home Screen and sending Web Push from a backend.
        </p>
      </Panel>

      <Panel icon={Download} title="Backup">
        <div className="button-row">
          <button className="secondary-button" onClick={exportJson}>
            <Download size={16} /> Export JSON
          </button>
          <label className="secondary-button file-button">
            <Upload size={16} /> Import JSON
            <input type="file" accept="application/json,.json" onChange={(event) => void importFile(event.target.files?.[0])} />
          </label>
        </div>
        {importError && <p className="danger-text">{importError}</p>}
      </Panel>
    </div>
  );
}

function ModuleScaffold({
  title,
  icon: Icon,
  count,
  notice,
  form,
  children,
}: {
  title: string;
  icon: LucideIcon;
  count: number;
  notice?: string;
  form: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="stack">
      <div className="module-header">
        <div>
          <p className="eyebrow">{count} active</p>
          <h2><Icon size={22} /> {title}</h2>
        </div>
        <button className="primary-button compact" onClick={() => setOpen(!open)}>
          {open ? <X size={16} /> : <Plus size={16} />}
          {open ? "Close" : "Add"}
        </button>
      </div>
      {notice && <div className="notice">{notice}</div>}
      {open && <Panel icon={Plus} title="Details">{form}</Panel>}
      {children}
    </div>
  );
}

function DashboardPanel<T>({
  icon,
  title,
  items,
  empty,
  more,
  onMore,
  render,
}: {
  icon: LucideIcon;
  title: string;
  items: T[];
  empty: string;
  more: number;
  onMore: () => void;
  render: (item: T) => ReactNode;
}) {
  return (
    <Panel icon={icon} title={title}>
      {items.length === 0 ? <p className="muted panel-empty">{empty}</p> : items.map(render)}
      {more > 0 && <button className="more-link" onClick={onMore}>+{more} more <ChevronRight size={14} /></button>}
    </Panel>
  );
}

function Panel({
  icon: Icon,
  title,
  action,
  children,
}: {
  icon: LucideIcon;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="panel">
      <div className="panel-title">
        <span><Icon size={18} /> {title}</span>
        {action}
      </div>
      {children}
    </section>
  );
}

function ItemLine({
  title,
  meta,
  tags,
  note,
  badge,
  tone,
  faded,
  action,
}: {
  title: string;
  meta?: string;
  tags?: string[];
  note?: string;
  badge?: string;
  tone?: "ok" | "warn" | "danger";
  faded?: boolean;
  action?: ReactNode;
}) {
  return (
    <article className={`item-line ${tone ?? ""} ${faded ? "faded" : ""}`}>
      <div className="item-main">
        <div className="item-title-row">
          <strong>{title}</strong>
          {badge && <em>{badge}</em>}
        </div>
        {tags?.length ? <div className="tags">{tags.map((tag) => <span key={tag}>{tag}</span>)}</div> : null}
        {meta && <p>{meta}</p>}
        {note && <small>{note}</small>}
      </div>
      {action}
    </article>
  );
}

function LearningCard({
  item,
  done,
  onLog,
  onProgress,
  onEdit,
  onDelete,
}: {
  item: LearningItem;
  done: boolean;
  onLog: (occurrenceId?: string) => void;
  onProgress: (id: string, progress: number) => Promise<void>;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [progress, setProgress] = useState(item.progress);
  return (
    <article className="learning-card">
      <div className="learning-head">
        <div>
          <strong>{item.title}</strong>
          <div className="tags"><span>{item.type}</span><span>{item.category}</span></div>
          <p className="muted">{scheduleSummary(item.schedule)}</p>
        </div>
        <RowActions done={done} onDone={() => onLog(item.schedule.windows[0]?.id)} onEdit={onEdit} onDelete={onDelete} />
      </div>
      <div className="progress-track"><span style={{ width: `${item.progress}%` }} /></div>
      <div className="progress-controls">
        <input type="range" min="0" max="100" value={progress} onChange={(event) => setProgress(Number(event.target.value))} />
        <b>{progress}%</b>
        <button className="secondary-button icon-only-text" onClick={() => void onProgress(item.id, progress)}>Save</button>
      </div>
      {item.notes && <p className="muted">{item.notes}</p>}
    </article>
  );
}

function RowActions({
  done,
  onDone,
  onEdit,
  onDelete,
}: {
  done?: boolean;
  onDone?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className="row-actions">
      {onDone && <DoneButton done={Boolean(done)} onClick={onDone} />}
      {onEdit && <IconButton label="Edit" icon={Edit3} onClick={onEdit} />}
      {onDelete && <IconButton label="Delete" icon={Trash2} onClick={onDelete} danger />}
    </div>
  );
}

function DoneButton({ done, onClick }: { done: boolean; onClick: () => void }) {
  return (
    <button className={done ? "done-button done" : "done-button"} onClick={onClick} aria-label={done ? "Done" : "Mark done"}>
      <Check size={17} />
    </button>
  );
}

function IconButton({
  label,
  icon: Icon,
  onClick,
  danger,
}: {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button className={danger ? "icon-button danger" : "icon-button"} onClick={onClick} aria-label={label} title={label}>
      <Icon size={16} />
    </button>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input type={type} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => <option key={option}>{option}</option>)}
      </select>
    </label>
  );
}

function ScheduleEditor({
  schedule,
  onChange,
}: {
  schedule: TaskSchedule;
  onChange: (schedule: TaskSchedule) => void;
}) {
  const updateWindow = (id: string, patch: Partial<TaskSchedule["windows"][number]>) => {
    onChange({
      ...schedule,
      windows: schedule.windows.map((window) => (window.id === id ? { ...window, ...patch } : window)),
    });
  };

  const removeWindow = (id: string) => {
    if (schedule.windows.length <= 1) return;
    onChange({ ...schedule, windows: schedule.windows.filter((window) => window.id !== id) });
  };

  const addWindow = () => {
    onChange({
      ...schedule,
      windows: [
        ...schedule.windows,
        { ...defaultWindow(`Window ${schedule.windows.length + 1}`, "18:00", "19:00"), id: newId() },
      ],
    });
  };

  const toggleWeekday = (day: number) => {
    const weekdays = schedule.weekdays.includes(day)
      ? schedule.weekdays.filter((value) => value !== day)
      : [...schedule.weekdays, day].sort();
    onChange({ ...schedule, weekdays });
  };

  return (
    <div className="schedule-editor">
      <div className="field">
        <span>Repeat</span>
        <div className="segmented">
          {(["daily", "interval", "weekly"] as const).map((frequency) => (
            <button
              type="button"
              key={frequency}
              className={schedule.frequency === frequency ? "active" : ""}
              onClick={() =>
                onChange({
                  ...schedule,
                  frequency,
                  weekdays: frequency === "weekly" && schedule.weekdays.length === 0 ? [new Date().getDay()] : schedule.weekdays,
                })
              }
            >
              {frequency === "daily" ? "Daily" : frequency === "interval" ? "Every N days" : "Weekdays"}
            </button>
          ))}
        </div>
      </div>

      {schedule.frequency === "interval" && (
        <div className="two-field-row">
          <TextField
            label="Every N days"
            type="number"
            value={String(schedule.interval_days)}
            onChange={(value) => onChange({ ...schedule, interval_days: Math.max(1, Number(value) || 1) })}
          />
          <TextField
            label="Start date"
            type="date"
            value={schedule.start_date}
            onChange={(start_date) => onChange({ ...schedule, start_date })}
          />
        </div>
      )}

      {schedule.frequency === "weekly" && (
        <div className="field">
          <span>Days</span>
          <div className="chips">
            {WEEKDAYS.map((day) => (
              <button
                key={day.value}
                type="button"
                className={schedule.weekdays.includes(day.value) ? "chip active" : "chip"}
                onClick={() => toggleWeekday(day.value)}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="field">
        <span>Time windows</span>
        <div className="window-stack">
          {schedule.windows.map((window) => (
            <div className="window-row" key={window.id}>
              <input
                aria-label="Window label"
                value={window.label}
                onChange={(event) => updateWindow(window.id, { label: event.target.value })}
              />
              <input
                aria-label="Start time"
                type="time"
                value={window.start_time}
                onChange={(event) => updateWindow(window.id, { start_time: event.target.value })}
              />
              <input
                aria-label="End time"
                type="time"
                value={window.end_time}
                onChange={(event) => updateWindow(window.id, { end_time: event.target.value })}
              />
              <label className="mini-toggle" title="Notify if missed">
                <input
                  type="checkbox"
                  checked={window.notify}
                  onChange={(event) => updateWindow(window.id, { notify: event.target.checked })}
                />
                <Bell size={14} />
              </label>
              <IconButton label="Remove window" icon={X} onClick={() => removeWindow(window.id)} danger />
            </div>
          ))}
        </div>
        <button className="secondary-button" type="button" onClick={addWindow}>
          <Plus size={16} /> Add window
        </button>
      </div>
    </div>
  );
}

function FormActions({ editing, onCancel }: { editing: boolean; onCancel: () => void }) {
  return (
    <div className="button-row">
      <button className="primary-button" type="submit">
        <Check size={16} /> {editing ? "Save changes" : "Save"}
      </button>
      {editing && <button className="secondary-button" type="button" onClick={onCancel}>Cancel</button>}
    </div>
  );
}

function BottomNav({
  active,
  reminders,
  onTab,
}: {
  active: Tab;
  reminders: Record<ModuleKey, number>;
  onTab: (tab: Tab) => void;
}) {
  return (
    <nav className="bottom-nav">
      {TABS.map(({ id, label, icon: Icon }) => {
        const badge = id === "today" ? Object.values(reminders).reduce((sum, value) => sum + value, 0) : reminders[id as ModuleKey];
        return (
          <button key={id} className={active === id ? "active" : ""} onClick={() => onTab(id)}>
            <span className="nav-icon">
              <Icon size={19} />
              {badge ? <b>{badge}</b> : null}
            </span>
            <span>{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function EmptyState({ icon: Icon, text }: { icon: LucideIcon; text: string }) {
  return (
    <div className="empty-state">
      <Icon size={34} />
      <p>{text}</p>
    </div>
  );
}

function Toast({ toast, onClose }: { toast: ToastState; onClose: () => void }) {
  return (
    <div className="toast">
      <span>{toast.message}</span>
      {toast.onAction && <button onClick={toast.onAction}><RotateCcw size={15} /> {toast.actionLabel}</button>}
      <button aria-label="Dismiss" onClick={onClose}><X size={15} /></button>
    </div>
  );
}

export default App;
